import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS, ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { Login as NewLogin } from '@cokitchen/cokitchen-auth';

import logo from 'assets/images/logo.png';
import { ENUM_ALL_ROLES } from 'utils/roles';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import useRoleContext from 'contexts/role.context';
import { LoginEnvType } from '@cokitchen/cokitchen-auth/dist/lib/login/types';
import { extractRoleAuthorization } from 'utils/authorization';
import { getDefaultAuthorizedRoute } from 'utils/route-authorization';
import { PermissionType } from 'models/permission.model';
import { apiService } from 'services/base.service';
import { updateLocalStorageUserData } from 'utils/functions';

const authEnvironment = process.env.REACT_APP_AUTH_ENVIRONMENT as LoginEnvType;

const LoginForm = () => {
  const navigate = useNavigate();
  const { setRoleAuthorization, setIsMainAdmin } = useRoleContext();

  const handleAdminLogin = (role: ENUM_ALL_ROLES, permissions: PermissionType[]) => {
    localStorage.setItem(STORAGE_KEYS.ADMIN_ROLE, role);
    setRoleAuthorization({
      roleName: ENUM_ALL_ROLES.CS_ADMIN,
      permissions
    });
    setIsMainAdmin(true);
    navigate(MAIN_STORE_ROUTE_KEYS.GENERAL_STORE_DASHBOARD);
  };

  const handleInvalidRole = () => {
    localStorage.clear();
    setRoleAuthorization();
    setIsMainAdmin(false);
    navigate(ROUTE_KEYS.LOGIN);
    toast(<ToastMessage type='error' message='Unrecognized login role!' />);
  };

  const getAuthorizationFromUserProfile = async () => {
    try {
      const response = await apiService.get('user/me');
      const user = response.data.data;
      updateLocalStorageUserData(user.user);
      return extractRoleAuthorization(user);
    } catch {
      return undefined;
    }
  };

  const handleLoginSuccess = async (res: any) => {
    if (res) {
      const fallbackAuthorization = extractRoleAuthorization(res?.data ?? res);
      const profileAuthorization = await getAuthorizationFromUserProfile();
      const { roleName, roleId, permissions } = profileAuthorization?.roleName
        ? profileAuthorization
        : fallbackAuthorization;

      if (!roleName) {
        handleInvalidRole();
        return;
      }

      const role = roleName as ENUM_ALL_ROLES;
      const isSuperAdminRole = [ENUM_ALL_ROLES.ADMIN, ENUM_ALL_ROLES.SUPER_ADMIN].includes(role);
      if (isSuperAdminRole) {
        handleAdminLogin(role, permissions);
        return;
      }
      setIsMainAdmin(false);
      setRoleAuthorization({ roleName, roleId, permissions });
      navigate(getDefaultAuthorizedRoute(permissions));
    }
  };

  return (
    <div className='login-container w-full bg-red-9 p-4 md:overflow-auto'>
      <div className='w-full flex justify-between items-center border-b border-b-[#EFF0F0] py-[27px] pl-4 pr-4 md:pl-[90px] md:pr-[82px]'>
        <img src={logo} alt='logo' />
        <Link to='/'>
          <p>Main store</p>
        </Link>
      </div>
      <section className='auth-login__card px-8 md:px-16 !shadow-none login_section'>
        <NewLogin env={authEnvironment} onLogin={handleLoginSuccess} title='Welcome back' />
      </section>
    </div>
  );
};

export default LoginForm;
